import { useCallback, useEffect, useRef } from 'react';
import { UpgradeWebModal } from 'design-system/UpgradeWebModal';
import { selectFeatureFlags } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { useGetIsKeyboardOpen } from 'hooks';
import { Keyboard } from 'react-native';
import { isWeb } from '../../../constants';
import { benefits } from '../benefits';
import { checkIsBenefitIdAvailableOnFree } from '../checkIsBenefitIdAvailableOnFree';
import { Tier } from '../entities';
import { isUnlocked } from '../isUnlocked';
import { selectUserTier } from '../selectors/tier';
import { useIsTierUnlockedForSpace } from './useIsTierUnlockedForSpace';
import PremiumFeatureMicroModal from '../components/PremiumFeatureMicroModal';
import { isMicroModalBenefitId } from '../components/PremiumFeatureMicroModalContent';
import { selectShouldShowMicroModal } from '../selectors';
import useV3MicroModal from './useV3MicroModal';
const KEYBOARD_CLOSE_DELAY = 50;
const useShouldShowUpgrade = ({ benefitId }) => {
    const { minTier } = benefits[benefitId];
    const isUnlockedForSpace = useIsTierUnlockedForSpace(minTier);
    const userTier = useAppSelector(selectUserTier);
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const showMicroModal = useAppSelector(selectShouldShowMicroModal);
    const v3MicroModal = useV3MicroModal();
    const getIsKeyboardOpen = useGetIsKeyboardOpen();
    const cancelFn = useRef(undefined);
    const timeoutRef = useRef(undefined);
    useEffect(() => () => {
        cancelFn.current?.();
        clearTimeout(timeoutRef.current);
    }, []);
    const navigation = useMainStackNavigation();
    const onPressUpgrade = useCallback(() => {
        if (minTier === Tier.free)
            return;
        if (isWeb) {
            UpgradeWebModal.show(minTier);
            return;
        }
        const showModal = () => {
            if (!showMicroModal)
                return;
            if (showMicroModal === 'v3') {
                v3MicroModal(minTier, benefitId);
                return;
            }
            PremiumFeatureMicroModal.show(() => navigation.navigateDeprecated('EmmaProModal', {
                tier: minTier,
                isTierRequired: true,
                benefitId,
            }), benefitId, showMicroModal);
        };
        if (showMicroModal && benefitId && isMicroModalBenefitId(benefitId)) {
            if (!getIsKeyboardOpen()) {
                showModal();
            }
            else {
                Keyboard.dismiss();
                timeoutRef.current = setTimeout(showModal, KEYBOARD_CLOSE_DELAY);
            }
            return;
        }
        navigation.navigateDeprecated('EmmaProModal', {
            tier: minTier,
            isTierRequired: true,
            benefitId,
        });
    }, [minTier, showMicroModal, benefitId, v3MicroModal, getIsKeyboardOpen]);
    const featureFlags = useAppSelector(selectFeatureFlags);
    const isAvailableOnFree = checkIsBenefitIdAvailableOnFree(benefitId, guessedHomeCountry, featureFlags);
    return {
        isUnlockedForSpace: isUnlockedForSpace || isAvailableOnFree,
        isUnlocked: isUnlocked(userTier, minTier) || isAvailableOnFree,
        isAvailableOnFree,
        onPressUpgrade,
    };
};
export default useShouldShowUpgrade;
